import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bug } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { z } from "zod";

const bugReportSchema = z.object({
  featureName: z.string().trim().min(1, { message: "Please select a feature" }),
  description: z.string()
    .trim()
    .min(10, { message: "Please provide at least 10 characters describing the issue" })
    .max(1000, { message: "Description must be less than 1000 characters" })
});

const FEATURE_OPTIONS = [
  "Easy ML Theory Questions",
  "Code Editor",
  "Code Execution",
  "Dataset Loading",
  "Other"
];

export const BugReportDialog = () => {
  const [open, setOpen] = useState(false);
  const [featureName, setFeatureName] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async () => {
    try {
      // Validate input
      const validatedData = bugReportSchema.parse({
        featureName,
        description
      });

      setIsSubmitting(true);

      const { error } = await supabase
        .from("bug_reports")
        .insert({
          user_id: user?.id || null,
          user_email: user?.email || null,
          feature_name: validatedData.featureName,
          description: validatedData.description
        });

      if (error) throw error;

      toast({
        title: "Bug report submitted",
        description: "Thank you for helping us improve! We'll look into this issue.",
      });

      // Reset form and close dialog
      setFeatureName("");
      setDescription("");
      setOpen(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to submit bug report. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Bug className="h-4 w-4" />
          Report a Bug
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Report a Bug</DialogTitle>
          <DialogDescription>
            Found something that's not working? Let us know and we'll fix it as soon as possible.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="feature">Feature</Label>
            <Select value={featureName} onValueChange={setFeatureName}>
              <SelectTrigger id="feature">
                <SelectValue placeholder="Select the feature with the issue" />
              </SelectTrigger>
              <SelectContent>
                {FEATURE_OPTIONS.map((feature) => (
                  <SelectItem key={feature} value={feature}>
                    {feature}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Please describe what went wrong and what you expected to happen..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[120px]"
              maxLength={1000}
            />
            <p className="text-sm text-muted-foreground">
              {description.length}/1000 characters
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit Report"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
